/**
 * Client for the widget's own Chubb backend (`api/chubb/autos/cotizar`, Vercel Function).
 * Hosts never provide a Chubb API.
 */

export interface ChubbQuotePayload {
  year: number
  brandName: string
  modelName: string
  amis: string
  postalCode: string
  gender: 'M' | 'F'
  birthDate: string
}

export interface ChubbProviderSession {
  quoteId: number
  quoteVersionId: number
  paymentTypeId: number
  packageId: number
  vehicleKey: string
}

export interface ChubbCobertura {
  primaNeta: number | null
  derechosPoliza: number | null
  iva: number | null
  recargos: number | null
  primaTotal: number | null
  primaTotalPrimerRecibo: number | null
}

export interface ChubbCoberturaItem {
  nombre: string
  sumaAsegurada: number | null
  deducible: number | null
  primaNeta: number | null
}

export interface ChubbQuoteSuccess {
  success: true
  source: 'chubb'
  mock: false
  cobertura: ChubbCobertura | null
  coberturas: ChubbCoberturaItem[]
  providerSession: ChubbProviderSession
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function readNullableNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function parseCobertura(raw: unknown): ChubbCobertura | null {
  if (!isRecord(raw)) return null
  return {
    primaNeta: readNullableNumber(raw.primaNeta),
    derechosPoliza: readNullableNumber(raw.derechosPoliza),
    iva: readNullableNumber(raw.iva),
    recargos: readNullableNumber(raw.recargos),
    primaTotal: readNullableNumber(raw.primaTotal),
    primaTotalPrimerRecibo: readNullableNumber(raw.primaTotalPrimerRecibo),
  }
}

function parseCoberturas(raw: unknown): ChubbCoberturaItem[] | null {
  if (!Array.isArray(raw)) return null
  return raw.map((row) => {
    if (!isRecord(row)) {
      return { nombre: '', sumaAsegurada: null, deducible: null, primaNeta: null }
    }
    return {
      nombre: typeof row.nombre === 'string' ? row.nombre : '',
      sumaAsegurada: readNullableNumber(row.sumaAsegurada),
      deducible: readNullableNumber(row.deducible),
      primaNeta: readNullableNumber(row.primaNeta),
    }
  })
}

function parseProviderSession(raw: unknown): ChubbProviderSession | null {
  if (!isRecord(raw)) return null
  const quoteId = readNullableNumber(raw.quoteId)
  const vehicleKey = typeof raw.vehicleKey === 'string' ? raw.vehicleKey : ''
  if (quoteId === null || !vehicleKey) return null
  return {
    quoteId,
    quoteVersionId: readNullableNumber(raw.quoteVersionId) ?? 0,
    paymentTypeId: readNullableNumber(raw.paymentTypeId) ?? 0,
    packageId: readNullableNumber(raw.packageId) ?? 0,
    vehicleKey,
  }
}

function errorMessage(body: Record<string, unknown> | null, fallback: string): string {
  const err = body?.error
  if (typeof err === 'string' && err.trim()) return err
  if (isRecord(err) && typeof err.message === 'string' && err.message.trim()) {
    return err.message
  }
  return fallback
}

/**
 * POST {widgetBase}/api/chubb/autos/cotizar.
 * Only accepts a real Chubb response (`source: chubb`, `mock: false`).
 */
export async function requestChubbQuoteAt(
  baseUrl: string,
  payload: ChubbQuotePayload,
): Promise<ChubbQuoteSuccess> {
  const base = baseUrl.replace(/\/$/, '')
  const response = await fetch(`${base}/api/chubb/autos/cotizar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = (await response.json().catch(() => null)) as Record<string, unknown> | null

  if (!response.ok || !body || body.success !== true) {
    throw new Error(errorMessage(body, 'No pudimos obtener la cotización de Chubb.'))
  }
  if (body.source !== 'chubb' || body.mock !== false) {
    throw new Error('Respuesta Chubb inválida (source/mock).')
  }

  const coberturas = parseCoberturas(body.coberturas)
  if (coberturas === null) {
    throw new Error('Respuesta Chubb inválida (coberturas).')
  }
  const providerSession = parseProviderSession(body.providerSession)
  if (!providerSession) {
    throw new Error('Respuesta Chubb inválida (falta providerSession).')
  }
  return {
    success: true,
    source: 'chubb',
    mock: false,
    cobertura: parseCobertura(body.cobertura ?? null),
    coberturas,
    providerSession,
  }
}
